import { useEffect, useMemo, useState } from "react";

import {
  controlMaxxPlayback,
  MAXX_PLAYBACK_STATE_EVENT,
  requestMaxxPlayback,
} from "./playbackController";

const EMPTY_STATE = {
  ready: false,
  duration: 0,
  progress: 0,
  time: 0,
  paused: false,
};

function normalizePlaybackState(state) {
  const clip = state?.clips?.find((entry) => Number(entry?.duration) > 0);
  if (!state?.active || !clip) return EMPTY_STATE;

  return {
    ready: true,
    duration: Number(clip.duration) || 0,
    progress: Math.max(0, Math.min(1, Number(clip.progress) || 0)),
    time: Math.max(0, Number(clip.time) || 0),
    paused: Boolean(clip.paused),
  };
}

function formatTime(seconds) {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  return `${minutes}:${String(safeSeconds % 60).padStart(2, "0")}`;
}

function RestartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5.5 8.3A7.7 7.7 0 1 1 4.8 15" />
      <path d="M5.5 3.8v4.8h4.8" />
    </svg>
  );
}

function PlayPauseIcon({ paused }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paused ? <path className="is-filled" d="m8 5 11 7-11 7z" /> : (
        <>
          <path d="M8.2 5.5v13" />
          <path d="M15.8 5.5v13" />
        </>
      )}
    </svg>
  );
}

export function MaxxPlaybackControls({ compact = false, obscured = false, visible = false }) {
  const [playback, setPlayback] = useState(EMPTY_STATE);

  useEffect(() => {
    if (!visible) {
      setPlayback(EMPTY_STATE);
      return undefined;
    }

    const syncState = (event) => {
      setPlayback(normalizePlaybackState(event?.detail ?? window.__maxxAnimationState));
    };

    syncState();
    window.addEventListener(MAXX_PLAYBACK_STATE_EVENT, syncState);
    const pollPlayback = window.setInterval(() => {
      controlMaxxPlayback("state");
    }, 250);
    return () => {
      window.clearInterval(pollPlayback);
      window.removeEventListener(MAXX_PLAYBACK_STATE_EVENT, syncState);
    };
  }, [visible]);

  const timeLabel = useMemo(
    () => `${formatTime(playback.time)} / ${formatTime(playback.duration)}`,
    [playback.duration, playback.time]
  );

  if (!visible) return null;

  const togglePlayback = () => {
    const action = playback.paused ? "resume" : "pause";
    requestMaxxPlayback(action, { source: "playback_controls" });
  };

  const seek = (progress) => {
    if (!controlMaxxPlayback("scrub_progress", progress)) return;
    setPlayback((current) => ({
      ...current,
      progress,
      time: current.duration * progress,
    }));
  };

  return (
    <aside
      className={`maxx-playback${compact ? " is-mobile" : ""}${playback.ready ? " is-ready" : ""}${obscured ? " is-obscured" : ""}`}
      aria-label="Neuron animation playback"
      aria-hidden={obscured ? "true" : undefined}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
    >
      <div className="maxx-playback__transport">
        <button
          type="button"
          className="maxx-playback__button"
          aria-label="Restart neuron animation"
          title="Restart"
          disabled={!playback.ready || obscured}
          onClick={() => controlMaxxPlayback("restart")}
        >
          <RestartIcon />
        </button>
        <button
          type="button"
          className="maxx-playback__button maxx-playback__button--primary"
          aria-label={playback.paused ? "Play neuron animation" : "Pause neuron animation"}
          aria-pressed={playback.paused}
          title={playback.paused ? "Play" : "Pause"}
          disabled={!playback.ready || obscured}
          onClick={togglePlayback}
        >
          <PlayPauseIcon paused={playback.paused} />
        </button>
      </div>
      <label className="maxx-playback__timeline">
        <input
          type="range"
          min="0"
          max="1000"
          step="1"
          value={Math.round(playback.progress * 1000)}
          aria-label="Neuron animation position"
          aria-valuetext={timeLabel}
          disabled={!playback.ready || obscured}
          onChange={(event) => seek(Number(event.target.value) / 1000)}
          style={{ "--maxx-playback-progress": `${playback.progress * 100}%` }}
        />
        <output>{playback.ready ? timeLabel : "Loading"}</output>
      </label>
    </aside>
  );
}
